import * as React from "react";
import {
    Box,
    Stack,
    Typography,
    Paper,
    Tabs,
    Tab,
    Button,
    TextField,
    MenuItem,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Switch,
    FormControlLabel,
    Checkbox,
    FormGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import { adminFetch } from "../services/adminFetch";

type PropertyRow = {
    id: number;
    title?: string;
    slug?: string;
    city?: string;
    region?: string;
    neighborhood?: string;
    address?: string;
    zip?: string;

    listing_status?: "rent" | "sale" | string;
    status?: "draft" | "published" | "archived" | string;
    property_type?: string;
    label?: string;

    persons_min?: number | null;
    persons_max?: number | null;

    bedrooms?: number | null;
    bathrooms?: number | null;
    size_m2?: number | null;

    latitude?: number | null;
    longitude?: number | null;

    featured_image_url?: string | null;

    price_per_night?: number | null;
    min_price_per_night?: number | null;
    min_stay_nights?: number | null;

    excerpt?: string | null;
    content?: string | null;
};

type ImageRow = {
    id: number;
    property_id: number;
    image_url: string; // normalized
    image_url_raw?: string;
    alt_text?: string | null;
    sort_order?: number;
};

type PriceRule = {
    id: number;
    property_id: number;
    months: number[];
    price_per_night: number;
    min_stay_nights: number | null;
    currency: string;
    label: string | null;
    priority: number;
    is_active: boolean;
};

type FeatureItem = { id: number; name: string };

function TabPanel(props: { value: number; index: number; children: React.ReactNode }) {
    const { value, index, children } = props;
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function numOrNull(v: any) {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function intOrNull(v: any) {
    if (v === "" || v === null || v === undefined) return null;
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : null;
}

async function jsonOrThrow(res: Response) {
    const data = await res.json().catch(() => null);
    if (!res.ok) {
        const msg = data?.error || data?.message || `HTTP ${res.status}`;
        throw new Error(msg);
    }
    return data;
}

export default function EditPropertyPage() {
    const { id } = useParams();
    const pid = Number(id);

    const [tab, setTab] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);

    const [item, setItem] = React.useState<PropertyRow | null>(null);

    // Images
    const [images, setImages] = React.useState<ImageRow[]>([]);
    const [imgBusy, setImgBusy] = React.useState(false);

    // Price rules
    const [rules, setRules] = React.useState<PriceRule[]>([]);
    const [rulesBusy, setRulesBusy] = React.useState(false);

    // Extras
    const [extras, setExtras] = React.useState<Array<{ title: string; value: string }>>([]);
    const [extrasBusy, setExtrasBusy] = React.useState(false);

    // Features
    const [featuresCatalog, setFeaturesCatalog] = React.useState<FeatureItem[]>([]);
    const [selectedFeatureIds, setSelectedFeatureIds] = React.useState<number[]>([]);
    const [featuresBusy, setFeaturesBusy] = React.useState(false);

    const [toast, setToast] = React.useState<{
        open: boolean;
        msg: string;
        severity: "success" | "error";
    }>({ open: false, msg: "", severity: "success" });

    // Add price rule dialog
    const [openRule, setOpenRule] = React.useState(false);
    const [newRule, setNewRule] = React.useState({
        monthsCsv: "",
        price_per_night: "",
        min_stay_nights: "",
        currency: "EUR",
        label: "",
        priority: "10",
        is_active: true,
    });

    const loadAll = React.useCallback(async () => {
        if (!pid || Number.isNaN(pid)) return;

        setLoading(true);
        setErr(null);
        try {
            // property
            const r1 = await adminFetch(`/api/admin/properties/${pid}`);
            const j1 = await jsonOrThrow(r1);
            setItem(j1.item);

            // images
            const r2 = await adminFetch(`/api/admin/properties/${pid}/images`);
            const j2 = await jsonOrThrow(r2);
            setImages(j2.items || []);

            // price rules
            const r3 = await adminFetch(`/api/admin/properties/${pid}/price-rules`);
            const j3 = await jsonOrThrow(r3);
            setRules(j3.items || []);

            // extras
            const r4 = await adminFetch(`/api/admin/properties/${pid}/extras`);
            const j4 = await jsonOrThrow(r4);
            setExtras((j4.items || []).map((x: any) => ({ title: x.title || "", value: x.value || "" })));

            // features catalog + selected
            const r5 = await adminFetch(`/api/admin/features`);
            const j5 = await jsonOrThrow(r5);
            setFeaturesCatalog(j5.items || []);

            const r6 = await adminFetch(`/api/admin/properties/${pid}/features`);
            const j6 = await jsonOrThrow(r6);
            setSelectedFeatureIds((j6.feature_ids || []).map((x: any) => Number(x)).filter((n: number) => !Number.isNaN(n)));
        } catch (e: any) {
            setErr(e?.message || "Load failed");
        } finally {
            setLoading(false);
        }
    }, [pid]);

    React.useEffect(() => {
        loadAll();
    }, [loadAll]);

    const update = (k: keyof PropertyRow, v: any) => {
        setItem((prev) => (prev ? { ...prev, [k]: v } : prev));
    };

    const saveBasics = async () => {
        if (!item) return;
        setSaving(true);
        setErr(null);
        try {
            const payload: any = {
                title: item.title || "",
                slug: item.slug || "",
                city: item.city || "",
                region: item.region || "",
                neighborhood: item.neighborhood || "",
                address: item.address || "",
                zip: item.zip || "",

                listing_status: item.listing_status || "rent",
                status: item.status || "draft",
                property_type: item.property_type || "apartment",
                label: item.label || "",

                persons_min: intOrNull(item.persons_min),
                persons_max: intOrNull(item.persons_max),

                bedrooms: intOrNull(item.bedrooms),
                bathrooms: intOrNull(item.bathrooms),
                size_m2: intOrNull(item.size_m2),

                latitude: numOrNull(item.latitude),
                longitude: numOrNull(item.longitude),

                price_per_night: numOrNull(item.price_per_night),
                min_price_per_night: numOrNull(item.min_price_per_night),
                min_stay_nights: intOrNull(item.min_stay_nights),

                excerpt: item.excerpt ?? null,
                content: item.content ?? null,
            };

            const res = await adminFetch(`/api/admin/properties/${pid}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const j = await jsonOrThrow(res);
            setItem(j.item);
            setToast({ open: true, msg: "Changes saved successfully", severity: "success" });

        } catch (e: any) {
            setErr(e?.message || "Save failed");
            setToast({ open: true, msg: e?.message || "Save failed", severity: "error" });

        } finally {
            setSaving(false);
        }
    };

    const [slugTouched, setSlugTouched] = React.useState(false);

    const slugify = (s: string) =>
        (s || "")
            .toLowerCase()
            .trim()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

    const onChangeTitle = (v: string) => {
        setItem(prev => {
            if (!prev) return prev;
            const next = { ...prev, title: v };
            if (!slugTouched) {
                next.slug = slugify(v);
            }
            return next;
        });
    };

    const onChangeSlug = (v: string) => {
        setSlugTouched(true);
        update("slug", slugify(v));
    };


    // ---------- IMAGES ----------
    const reloadImages = async () => {
        const r = await adminFetch(`/api/admin/properties/${pid}/images`);
        const j = await jsonOrThrow(r);
        setImages(j.items || []);
    };

    const uploadImage = async (file: File) => {
        setImgBusy(true);
        setErr(null);
        try {
            const fd = new FormData();
            fd.append("file", file);

            const res = await adminFetch(`/api/admin/properties/${pid}/images/upload`, {
                method: "POST",
                body: fd,
            });

            await jsonOrThrow(res);
            await reloadImages();
        } catch (e: any) {
            setErr(e?.message || "Upload failed");
        } finally {
            setImgBusy(false);
        }
    };

    const deleteImage = async (imageId: number) => {
        setImgBusy(true);
        setErr(null);
        try {
            const res = await adminFetch(`/api/admin/properties/${pid}/images/${imageId}`, {
                method: "DELETE",
            });
            await jsonOrThrow(res);
            await reloadImages();
        } catch (e: any) {
            setErr(e?.message || "Delete failed");
        } finally {
            setImgBusy(false);
        }
    };

    // ---------- PRICE RULES ----------
    const reloadRules = async () => {
        const r = await adminFetch(`/api/admin/properties/${pid}/price-rules`);
        const j = await jsonOrThrow(r);
        setRules(j.items || []);
    };

    const addRule = async () => {
        setRulesBusy(true);
        setErr(null);
        try {
            const months = (newRule.monthsCsv || "")
                .split(",")
                .map((x) => parseInt(x.trim(), 10))
                .filter((n) => Number.isFinite(n) && n >= 1 && n <= 12);

            const res = await adminFetch(`/api/admin/properties/${pid}/price-rules`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    months,
                    price_per_night: Number(newRule.price_per_night),
                    min_stay_nights: newRule.min_stay_nights ? parseInt(newRule.min_stay_nights, 10) : null,
                    currency: (newRule.currency || "EUR").toUpperCase(),
                    label: newRule.label ? newRule.label : null,
                    priority: parseInt(newRule.priority || "10", 10) || 10,
                    is_active: !!newRule.is_active,
                }),
            });

            await jsonOrThrow(res);
            setOpenRule(false);
            setNewRule({ monthsCsv: "", price_per_night: "", min_stay_nights: "", currency: "EUR", label: "", priority: "10", is_active: true });
            await reloadRules();
        } catch (e: any) {
            setErr(e?.message || "Add rule failed");
        } finally {
            setRulesBusy(false);
        }
    };

    const toggleRuleActive = async (rule: PriceRule) => {
        setRulesBusy(true);
        setErr(null);
        try {
            const res = await adminFetch(`/api/admin/price-rules/${rule.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ is_active: !rule.is_active }),
            });
            await jsonOrThrow(res);
            await reloadRules();
        } catch (e: any) {
            setErr(e?.message || "Update rule failed");
        } finally {
            setRulesBusy(false);
        }
    };

    const deleteRule = async (ruleId: number) => {
        setRulesBusy(true);
        setErr(null);
        try {
            const res = await adminFetch(`/api/admin/price-rules/${ruleId}`, { method: "DELETE" });
            await jsonOrThrow(res);
            await reloadRules();
        } catch (e: any) {
            setErr(e?.message || "Delete rule failed");
        } finally {
            setRulesBusy(false);
        }
    };

    // ---------- EXTRAS ----------
    const saveExtras = async () => {
        setExtrasBusy(true);
        setErr(null);
        try {
            const clean = extras
                .map((x) => ({ title: (x.title || "").trim(), value: (x.value || "").trim() }))
                .filter((x) => x.title || x.value);

            const res = await adminFetch(`/api/admin/properties/${pid}/extras`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ items: clean }),
            });

            await jsonOrThrow(res);
            // reload
            const r2 = await adminFetch(`/api/admin/properties/${pid}/extras`);
            const j2 = await jsonOrThrow(r2);
            setExtras((j2.items || []).map((x: any) => ({ title: x.title || "", value: x.value || "" })));
        } catch (e: any) {
            setErr(e?.message || "Save extras failed");
        } finally {
            setExtrasBusy(false);
        }
    };

    // ---------- FEATURES ----------
    const saveFeatures = async () => {
        setFeaturesBusy(true);
        setErr(null);
        try {
            const res = await adminFetch(`/api/admin/properties/${pid}/features`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ feature_ids: selectedFeatureIds }),
            });
            await jsonOrThrow(res);
        } catch (e: any) {
            setErr(e?.message || "Save features failed");
        } finally {
            setFeaturesBusy(false);
        }
    };

    const header = (
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 1000 }}>
                    Edit property
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    ID #{pid} • {item?.title || "—"}
                </Typography>
            </Box>

            <Button
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={saveBasics}
                disabled={saving || loading || !item}
                sx={{ borderRadius: 3 }}
            >
                Save
            </Button>
        </Stack>
    );

    if (loading) {
        return (
            <Stack spacing={2}>
                {header}
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography>Loading…</Typography>
                    </Stack>
                </Paper>
            </Stack>
        );
    }

    if (!item) {
        return (
            <Stack spacing={2}>
                {header}
                <Alert severity="error">{err || "Property not found"}</Alert>
            </Stack>
        );
    }

    return (
        <Stack spacing={2.2}>
            {header}

            {err ? <Alert severity="warning">{err}</Alert> : null}

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ px: 1 }}
                >
                    <Tab label="Basics" />
                    <Tab label="Images" />
                    <Tab label="Season pricing" />
                    <Tab label="Extras" />
                    <Tab label="Features" />
                </Tabs>
                <Divider />

                {/* BASICS */}
                <TabPanel value={tab} index={0}>
                    <Box sx={{ p: 2.2 }}>
                        <Stack spacing={2.2}>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <TextField
                                    label="Title"
                                    value={item.title || ""}
                                    onChange={(e) => onChangeTitle(e.target.value)}
                                    fullWidth
                                />

                                <TextField
                                    label="Slug"
                                    value={item.slug || ""}
                                    onChange={(e) => onChangeSlug(e.target.value)}
                                    helperText="Auto-generated from title unless you edit it"
                                    fullWidth
                                />
                            </Stack>

                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <TextField
                                    select
                                    label="Listing"
                                    value={item.listing_status || "rent"}
                                    onChange={(e) => update("listing_status", e.target.value)}
                                    fullWidth
                                >
                                    <MenuItem value="rent">Rent</MenuItem>
                                    <MenuItem value="sale">Sale</MenuItem>
                                </TextField>

                                <TextField
                                    select
                                    label="Status"
                                    value={item.status || "draft"}
                                    onChange={(e) => update("status", e.target.value)}
                                    fullWidth
                                >
                                    <MenuItem value="draft">Draft</MenuItem>
                                    <MenuItem value="published">Published</MenuItem>
                                    <MenuItem value="archived">Archived</MenuItem>
                                </TextField>

                                <TextField
                                    label="Property type"
                                    value={item.property_type || ""}
                                    onChange={(e) => update("property_type", e.target.value)}
                                    fullWidth
                                />

                                <TextField
                                    label="Label"
                                    value={item.label || ""}
                                    onChange={(e) => update("label", e.target.value)}
                                    fullWidth
                                />
                            </Stack>

                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <TextField label="City" value={item.city || ""} onChange={(e) => update("city", e.target.value)} fullWidth />
                                <TextField label="Region" value={item.region || ""} onChange={(e) => update("region", e.target.value)} fullWidth />
                                <TextField label="Neighborhood" value={item.neighborhood || ""} onChange={(e) => update("neighborhood", e.target.value)} fullWidth />
                            </Stack>

                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <TextField label="Address" value={item.address || ""} onChange={(e) => update("address", e.target.value)} fullWidth />
                                <TextField label="ZIP" value={item.zip || ""} onChange={(e) => update("zip", e.target.value)} sx={{ width: { md: 220 } }} />
                            </Stack>

                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <TextField label="Persons min" type="number" value={item.persons_min ?? ""} onChange={(e) => update("persons_min", e.target.value)} sx={{ width: { md: 200 } }} />
                                <TextField label="Persons max" type="number" value={item.persons_max ?? ""} onChange={(e) => update("persons_max", e.target.value)} sx={{ width: { md: 200 } }} />
                                <Box sx={{ flex: 1 }} />
                                <Chip
                                    label={`Capacity: ${item.persons_min != null || item.persons_max != null
                                        ? `${item.persons_min ?? "?"}–${item.persons_max ?? "?"}`
                                        : "—"
                                        }`}
                                    variant="outlined"
                                    sx={{ alignSelf: { md: "center" } }}
                                />
                            </Stack>

                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <TextField label="Bedrooms" type="number" value={item.bedrooms ?? ""} onChange={(e) => update("bedrooms", e.target.value)} sx={{ width: { md: 200 } }} />
                                <TextField label="Bathrooms" type="number" value={item.bathrooms ?? ""} onChange={(e) => update("bathrooms", e.target.value)} sx={{ width: { md: 200 } }} />
                                <TextField label="Size m²" type="number" value={item.size_m2 ?? ""} onChange={(e) => update("size_m2", e.target.value)} sx={{ width: { md: 200 } }} />
                            </Stack>

                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <TextField label="Latitude" value={item.latitude ?? ""} onChange={(e) => update("latitude", e.target.value)} sx={{ width: { md: 240 } }} />
                                <TextField label="Longitude" value={item.longitude ?? ""} onChange={(e) => update("longitude", e.target.value)} sx={{ width: { md: 240 } }} />
                            </Stack>

                            <Divider />

                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <TextField label="Price per night" type="number" value={item.price_per_night ?? ""} onChange={(e) => update("price_per_night", e.target.value)} sx={{ width: { md: 240 } }} />
                                <TextField label="Min price per night" type="number" value={item.min_price_per_night ?? ""} onChange={(e) => update("min_price_per_night", e.target.value)} sx={{ width: { md: 240 } }} />
                                <TextField label="Min stay nights" type="number" value={item.min_stay_nights ?? ""} onChange={(e) => update("min_stay_nights", e.target.value)} sx={{ width: { md: 240 } }} />
                            </Stack>

                            <TextField
                                label="Excerpt"
                                value={item.excerpt ?? ""}
                                onChange={(e) => update("excerpt", e.target.value)}
                                multiline
                                minRows={2}
                            />
                            <TextField
                                label="Content"
                                value={item.content ?? ""}
                                onChange={(e) => update("content", e.target.value)}
                                multiline
                                minRows={5}
                            />
                        </Stack>
                    </Box>
                </TabPanel>

                {/* IMAGES */}
                <TabPanel value={tab} index={1}>
                    <Box sx={{ p: 2.2 }}>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    component="label"
                                    startIcon={<UploadIcon />}
                                    variant="contained"
                                    disabled={imgBusy}
                                    sx={{ borderRadius: 3 }}
                                >
                                    Upload image
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) uploadImage(f);
                                            e.currentTarget.value = "";
                                        }}
                                    />
                                </Button>
                                {imgBusy ? <CircularProgress size={18} /> : null}
                            </Stack>

                            <Stack spacing={1.2}>
                                {images.length === 0 ? (
                                    <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3 }}>
                                        <Typography color="text.secondary">No images yet.</Typography>
                                    </Paper>
                                ) : (
                                    images.map((im) => (
                                        <Paper
                                            key={im.id}
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 3,
                                                display: "flex",
                                                gap: 1.5,
                                                alignItems: "center",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 110,
                                                    height: 74,
                                                    borderRadius: 2,
                                                    overflow: "hidden",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    bgcolor: "background.default",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                                                <img
                                                    src={im.image_url}
                                                    alt={im.alt_text || "Property image"}
                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                />
                                            </Box>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 900 }} noWrap>
                                                    #{im.id}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    {im.image_url_raw || im.image_url}
                                                </Typography>
                                            </Box>

                                            <IconButton onClick={() => deleteImage(im.id)} disabled={imgBusy}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Paper>
                                    ))
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                </TabPanel>

                {/* PRICING RULES */}
                <TabPanel value={tab} index={2}>
                    <Box sx={{ p: 2.2 }}>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="contained"
                                    onClick={() => setOpenRule(true)}
                                    disabled={rulesBusy}
                                    sx={{ borderRadius: 3 }}
                                >
                                    Add rule
                                </Button>
                                {rulesBusy ? <CircularProgress size={18} /> : null}
                            </Stack>

                            {rules.length === 0 ? (
                                <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3 }}>
                                    <Typography color="text.secondary">No season rules yet.</Typography>
                                </Paper>
                            ) : (
                                <Stack spacing={1.2}>
                                    {rules.map((r) => (
                                        <Paper key={r.id} variant="outlined" sx={{ p: 1.8, borderRadius: 3 }}>
                                            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={{ fontWeight: 1000 }}>
                                                        {r.label || "Season rule"} • {r.price_per_night} {r.currency}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Months: {r.months.join(", ")} • Min stay: {r.min_stay_nights ?? "—"} • Priority: {r.priority}
                                                    </Typography>
                                                </Box>

                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={!!r.is_active}
                                                            onChange={() => toggleRuleActive(r)}
                                                            disabled={rulesBusy}
                                                        />
                                                    }
                                                    label={r.is_active ? "Active" : "Inactive"}
                                                />

                                                <IconButton onClick={() => deleteRule(r.id)} disabled={rulesBusy}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}

                            <Dialog open={openRule} onClose={() => setOpenRule(false)} fullWidth maxWidth="sm">
                                <DialogTitle>Add season rule</DialogTitle>
                                <DialogContent sx={{ pt: 1 }}>
                                    <Stack spacing={2} sx={{ mt: 1 }}>
                                        <TextField
                                            label="Months (comma separated, e.g. 6,7,8)"
                                            value={newRule.monthsCsv}
                                            onChange={(e) => setNewRule((s) => ({ ...s, monthsCsv: e.target.value }))}
                                        />
                                        <TextField
                                            label="Price per night"
                                            type="number"
                                            value={newRule.price_per_night}
                                            onChange={(e) => setNewRule((s) => ({ ...s, price_per_night: e.target.value }))}
                                        />
                                        <TextField
                                            label="Min stay nights (optional)"
                                            type="number"
                                            value={newRule.min_stay_nights}
                                            onChange={(e) => setNewRule((s) => ({ ...s, min_stay_nights: e.target.value }))}
                                        />
                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                label="Currency"
                                                value={newRule.currency}
                                                onChange={(e) => setNewRule((s) => ({ ...s, currency: e.target.value }))}
                                                sx={{ width: 160 }}
                                            />
                                            <TextField
                                                label="Priority"
                                                type="number"
                                                value={newRule.priority}
                                                onChange={(e) => setNewRule((s) => ({ ...s, priority: e.target.value }))}
                                                sx={{ width: 160 }}
                                            />
                                        </Stack>
                                        <TextField
                                            label="Label (optional)"
                                            value={newRule.label}
                                            onChange={(e) => setNewRule((s) => ({ ...s, label: e.target.value }))}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={!!newRule.is_active}
                                                    onChange={(e) => setNewRule((s) => ({ ...s, is_active: e.target.checked }))}
                                                />
                                            }
                                            label="Active"
                                        />
                                    </Stack>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setOpenRule(false)}>Cancel</Button>
                                    <Button variant="contained" onClick={addRule} disabled={rulesBusy}>
                                        Create
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Stack>
                    </Box>
                </TabPanel>

                {/* EXTRAS */}
                <TabPanel value={tab} index={3}>
                    <Box sx={{ p: 2.2 }}>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="outlined"
                                    onClick={() => setExtras((x) => [...x, { title: "", value: "" }])}
                                    sx={{ borderRadius: 3 }}
                                >
                                    Add extra
                                </Button>

                                <Box sx={{ flex: 1 }} />

                                <Button
                                    startIcon={<SaveIcon />}
                                    variant="contained"
                                    onClick={saveExtras}
                                    disabled={extrasBusy}
                                    sx={{ borderRadius: 3 }}
                                >
                                    Save extras
                                </Button>

                                {extrasBusy ? <CircularProgress size={18} /> : null}
                            </Stack>

                            {extras.length === 0 ? (
                                <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3 }}>
                                    <Typography color="text.secondary">No extras yet.</Typography>
                                </Paper>
                            ) : (
                                <Stack spacing={1.2}>
                                    {extras.map((ex, idx) => (
                                        <Paper key={idx} variant="outlined" sx={{ p: 1.6, borderRadius: 3 }}>
                                            <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ md: "center" }}>
                                                <TextField
                                                    label="Title"
                                                    value={ex.title}
                                                    onChange={(e) =>
                                                        setExtras((arr) =>
                                                            arr.map((a, i) => (i === idx ? { ...a, title: e.target.value } : a))
                                                        )
                                                    }
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Value"
                                                    value={ex.value}
                                                    onChange={(e) =>
                                                        setExtras((arr) =>
                                                            arr.map((a, i) => (i === idx ? { ...a, value: e.target.value } : a))
                                                        )
                                                    }
                                                    fullWidth
                                                />
                                                <IconButton
                                                    onClick={() => setExtras((arr) => arr.filter((_, i) => i !== idx))}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </Box>
                </TabPanel>

                {/* FEATURES */}
                <TabPanel value={tab} index={4}>
                    <Box sx={{ p: 2.2 }}>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    startIcon={<SaveIcon />}
                                    variant="contained"
                                    onClick={saveFeatures}
                                    disabled={featuresBusy}
                                    sx={{ borderRadius: 3 }}
                                >
                                    Save features
                                </Button>
                                {featuresBusy ? <CircularProgress size={18} /> : null}
                            </Stack>

                            <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3 }}>
                                <Typography sx={{ fontWeight: 1000, mb: 1 }}>
                                    Features catalog
                                </Typography>

                                {featuresCatalog.length === 0 ? (
                                    <Typography color="text.secondary">No features found.</Typography>
                                ) : (
                                    <FormGroup>
                                        {featuresCatalog.map((f) => {
                                            const checked = selectedFeatureIds.includes(f.id);
                                            return (
                                                <FormControlLabel
                                                    key={f.id}
                                                    control={
                                                        <Checkbox
                                                            checked={checked}
                                                            onChange={(e) => {
                                                                const on = e.target.checked;
                                                                setSelectedFeatureIds((prev) => {
                                                                    if (on) return Array.from(new Set([...prev, f.id]));
                                                                    return prev.filter((x) => x !== f.id);
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={f.name}
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                )}
                            </Paper>
                        </Stack>
                    </Box>
                </TabPanel>

            </Paper>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast(t => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={toast.severity} variant="filled">
                    {toast.msg}
                </Alert>
            </Snackbar>

            {/* tiny tip */}
            <Typography variant="caption" color="text.secondary">
                Tip: αν θες i18n αργότερα, εδώ αλλάζουμε μόνο labels/strings σε keys (π.χ. "admin.properties.title").
            </Typography>
        </Stack>
    );
}
